import * as React from 'react';
import { MemoIcon, TriangleExclamationIcon } from '@revfluence/fresh-icons/regular/esm';
import { message } from '@revfluence/fresh';
import { TProject } from '@frontend/app/containers/Projects/types';
import Terms from '@frontend/app/containers/Projects/TermsPage/components/Terms';
import TermsTemplate from '@frontend/app/containers/Projects/TermsPage/components/TermsTemplate';
import {
  useGetDefaultProgramTemplate,
  useSetDefaultProgramTemplateMutation,
  useGetTermsTemplatesByProgramId,
  useGetTermsName,
} from '@frontend/app/hooks';
import { GetDefaultProgramTemplateQuery_defaultTemplate as ITermsTemplate } from '@frontend/app/queries/types/GetDefaultProgramTemplateQuery';
import { TermsDrawer } from '@frontend/applications/TermsApp/components/shared/Drawer/TermsDrawer';
import {
  TermsType,
} from '@frontend/app/types/globalTypes';
import { CircleCheckIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { P } from '@frontend/shadcn/components/typography/p';
import { H4 } from '@frontend/shadcn/components/typography/h4';
import { ClientFeature } from '@frontend/app/constants/clientFeatures';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { GET_DEFAULT_PROGRAM_TEMPLATE, GET_TERMS_SETTINGS_FOR_PROGRAM } from '@frontend/app/queries';
import { SectionCard } from '../../CampaignPage/components/SectionCard';
import { SelectableCardGroup, ISelectableCardOption } from '../components/SelectableCardGroup';

const {
  useState,
  useCallback,
  useEffect,
  useMemo,
} = React;

interface IBasicAdvancedTermsSettingsPageProps {
  project: TProject;
  showFlexUI?: boolean;
}

const BasicAdvancedTermsSettingsPage: React.FC<IBasicAdvancedTermsSettingsPageProps> = React.memo(({ project, showFlexUI = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<ITermsTemplate | null>(null);
  const [editing, setEditing] = useState(false);
  const { pluralTermsName } = useGetTermsName();
  const title = pluralTermsName;

  const {
    [ClientFeature.BASIC_TERMS]: isBasicTermsEnabled,
  } = useClientFeatures();

  const {
    loading: templatesLoading,
    data: {
      templates = null,
    } = {},
  } = useGetTermsTemplatesByProgramId({
    variables: {
      programId: project.id,
    },
    skip: !project.id,
    fetchPolicy: 'no-cache',
  });

  const {
    loading: defaultTemplateLoading,
    data: {
      defaultTemplate = null,
    } = {},
  } = useGetDefaultProgramTemplate({
    variables: {
      programId: project.id,
    },
    skip: !project.id,
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate);
    }
  }, [
    defaultTemplate,
  ]);

  const [setDefaultProgramTemplate, {
    loading: setDefaultProgramTemplateLoading,
  }] = useSetDefaultProgramTemplateMutation({
    onCompleted(result) {
      const { newDefaultProgramTemplate } = result;
      setSelectedTemplate(newDefaultProgramTemplate);
       const text = newDefaultProgramTemplate.termsType === TermsType.BASIC ? `Flexible ${pluralTermsName}` : `Standard ${pluralTermsName}`;
       message.success({
         icon: <CircleCheckIcon />,
         content: `Default set to ${text}`,
       });
    },
    onError(error) {
      message.error({
        icon: <TriangleExclamationIcon />,
        content: error.message,
      });
    },
  });

  const termsType = useMemo(() => {
    if (selectedTemplate) {
      return selectedTemplate.termsType;
    }
    return null;
  }, [
    selectedTemplate,
  ]);

  const selectTemplateHandler = useCallback(
    (termsTemplateId: number) => {
      if (selectedTemplate && termsTemplateId === selectedTemplate.id) {
        return;
      }
      setDefaultProgramTemplate({
        variables: {
          programId: project.id,
          termsTemplateId,
        },
        refetchQueries: [
          GET_DEFAULT_PROGRAM_TEMPLATE,
          GET_TERMS_SETTINGS_FOR_PROGRAM,
        ],
      });
    }, [
      selectedTemplate,
      project.id,
      setDefaultProgramTemplate,
    ],
  );

  const editTemplateHandler = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setEditing(true);
  }, []);

  const handleEdit = useCallback((selectedId: number) => {
    const templateToEdit = templates?.find((t) => t.id === selectedId);
    if (templateToEdit) {
      setEditing(true);
    }
  }, [templates]);

  const isLoading = defaultTemplateLoading || templatesLoading || setDefaultProgramTemplateLoading;

  // Find the template IDs for standard and flexible templates
  const standardTemplate = templates?.find((t) => t.termsType === TermsType.ADVANCED);
  const flexibleTemplate = templates?.find((t) => t.termsType === TermsType.BASIC);

  // Prepare options for the generic component
  const cardOptions = useMemo(() => {
    const options: ISelectableCardOption<number>[] = [];
    if (standardTemplate) {
      options.push({
        id: 'standard',
        value: standardTemplate.id,
        disabled: !standardTemplate.id,
        renderBody: (
          <>
            <H4 className="font-semibold mb-0">
              Standard {pluralTermsName.slice(0, -1)}
            </H4>
            <P className="text-primaryMuted">
              Best for Influencer and Custom Campaigns
            </P>
            <Button
              className="pl-0"
              variant="link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEdit(standardTemplate.id);
              }}
              disabled={selectedTemplate?.id !== standardTemplate.id}
              tabIndex={-1}
            >
              Edit Template
            </Button>
          </>
        ),
      });
    }
    if (isBasicTermsEnabled && flexibleTemplate) {
      options.push({
        id: 'flexible',
        value: flexibleTemplate.id,
        disabled: !flexibleTemplate.id,
        renderBody: (
          <>
            <H4 className="font-semibold mb-0">
              Flexible {pluralTermsName.slice(0, -1)}
            </H4>
            <P className="text-primaryMuted">
              Best for Seeding and Ambassador Campaigns
            </P>
            <Button
              variant="link"
              className="pl-0"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleEdit(flexibleTemplate.id);
              }}
              disabled={selectedTemplate?.id !== flexibleTemplate.id}
              tabIndex={-1}
            >
              Edit Template
            </Button>
          </>
        ),
      });
    }
    return options;
  }, [pluralTermsName, standardTemplate, flexibleTemplate, handleEdit, selectedTemplate?.id, isBasicTermsEnabled]);

  // Get the ID of the selected template
  const selectedTemplateId = selectedTemplate?.id ?? null;

  if (showFlexUI) {
    return (
      <SectionCard
        icon={<MemoIcon />}
        subtitle={pluralTermsName}
        title={`Set Your Default ${pluralTermsName.slice(0, -1)}`}
        description={`Your selection will be the default each time you send ${pluralTermsName.toLowerCase()} in this project.`}
      >
        {editing && (
          <TermsDrawer
            onClose={() => setEditing(false)}
            title={
              termsType === TermsType.BASIC
                ? `Edit Flexible ${pluralTermsName} Template`
                : `Edit Standard ${pluralTermsName} Template`
            }
            open
            showFlexUI
          >
            <TermsTemplate
              isEditing={false}
              memberIds={[]}
              termsType={termsType}
              projectId={project.id}
              onClose={() => setEditing(false)}
              onSwitchTermsType={() => {}}
              showFlexUI
            />
          </TermsDrawer>
        )}

        {isLoading ? (
          <Skeleton className="w-full h-[100px] rounded-2xl" />
        ) : (
          <SelectableCardGroup<number>
            value={selectedTemplateId}
            options={cardOptions}
            onChange={selectTemplateHandler}
          />
        )}
      </SectionCard>
    );
  }

  return (
    <>
      {editing && (
        <TermsDrawer
          onClose={() => setEditing(false)}
          title={
            termsType === TermsType.BASIC
              ? `Edit Flexible ${pluralTermsName} Template`
              : `Edit Standard ${pluralTermsName} Template`
            }
          open
        >
          <TermsTemplate
            isEditing={false}
            memberIds={[]}
            termsType={termsType}
            projectId={project.id}
            onClose={() => setEditing(false)}
            onSwitchTermsType={() => {}}
          />
        </TermsDrawer>
      )}
      <Terms
        title={title}
        selectedTermsType={termsType}
        templates={templates}
        loading={isLoading}
        onSelectTemplate={selectTemplateHandler}
        onEditTemplate={editTemplateHandler}
      />
    </>
  );
});

BasicAdvancedTermsSettingsPage.displayName = 'BasicAdvancedTermsSettingsPage';

export default BasicAdvancedTermsSettingsPage;
